import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CommsProvider } from './context/CommsContext';
import { LoginForm } from './components/shared/LoginForm';
import { ProtectedRoute } from './components/shared/ProtectedRoute';
import { LoadingSpinner } from './components/ui';

// Lazy load page components
const LandingPage = lazy(() => import('./pages/LandingPage'));
const Dashboard = lazy(() => import('./pages/Dashboard').then(module => ({ default: module.Dashboard })));
const Define = lazy(() => import('./pages/Define').then(module => ({ default: module.Define })));
const Draft = lazy(() => import('./pages/Draft').then(module => ({ default: module.Draft })));
const Send = lazy(() => import('./pages/Send').then(module => ({ default: module.Send })));
const CommsAnalytics = lazy(() => import('./pages/CommsAnalytics').then(module => ({ default: module.Analytics })));
const DomainInsights = lazy(() => import('./pages/DomainInsights'));
const LAUNCHProcess = lazy(() => import('./pages/LAUCHProcess'));
const AiCapitalInstitute = lazy(() => import('./pages/AiCapitalInstitute'));
const AiCapitalInstituteGetStarted = lazy(() => import('./pages/AiCapitalInstitute/AiCapitalInstitueGetStarted'));
const AiCapitalInstituteRegisterUser = lazy(() => import('./pages/AiCapitalInstitute/AiCapitalInstituteRegisterUser'));
const AiCapitalInstitutePortal = lazy(() => import('./pages/AiCapitalInstitute/AiCpaitalInstitutePortal'));
const PersonSearch = lazy(() => import('./pages/PersonSearch').then(module => ({ default: module.PersonSearch })));
const CapTables = lazy(() => import('./pages/CapTables').then(module => ({ default: module.CapTables })));
const ProfitLoss = lazy(() => import('./pages/ProfitLoss').then(module => ({ default: module.ProfitLoss })));
const Projects = lazy(() => import('./pages/Projects').then(module => ({ default: module.Projects })));
const UsersAdmin = lazy(() => import('./pages/UsersAdmin').then(module => ({ default: module.UsersAdmin })));
const CampaignDashboard = lazy(() => import('./pages/CampaignDashboard').then(module => ({ default: module.CampaignDashboard })));
const Campaigns = lazy(() => import('./pages/Campaigns').then(module => ({ default: module.Campaigns })));
const RecipientLists = lazy(() => import('./pages/RecipientLists').then(module => ({ default: module.RecipientLists })));

// Loading fallback component
const PageLoadingSpinner: React.FC = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
      <LoadingSpinner size="lg" />
      <p className="mt-4 text-gray-600">Loading...</p>
    </div>
  </div>
);


// Main app content
const AppContent: React.FC = () => {
  const { isLoading } = useAuth();

  console.log('AppContent - isLoading:', isLoading);

  if (isLoading) {
    return <PageLoadingSpinner />;
  }

  console.log('Rendering routes');
  return (
    <CommsProvider>
      <Suspense fallback={<PageLoadingSpinner />}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginForm />} />
          
          {/* COMMS section - Admin only */}
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']}>
                <Dashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/define" 
            element={
              <ProtectedRoute requiredRoles={['admin']}>
                <Define />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/draft" 
            element={
              <ProtectedRoute requiredRoles={['admin']}>
                <Draft />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/send" 
            element={
              <ProtectedRoute requiredRoles={['admin']}>
                <Send />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/comms-analytics" 
            element={
              <ProtectedRoute requiredRoles={['admin']}>
                <CommsAnalytics />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/domain-insights" 
            element={
              <ProtectedRoute requiredRoles={['admin']}>
                <DomainInsights />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/launch-process" 
            element={
              <ProtectedRoute requiredRoles={['admin', 'investor']}>
                <LAUNCHProcess />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/ai-capital-institute" 
            element={
              <ProtectedRoute requiredRoles={['admin']}>
                <AiCapitalInstitute />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/ai-capital-institute-get-started" 
            element={
              <ProtectedRoute requiredRoles={['admin_todo']}>
                <AiCapitalInstituteGetStarted />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/ai-capital-institute-register-user" 
            element={
              <ProtectedRoute requiredRoles={['admin']}>
                <AiCapitalInstituteRegisterUser />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/ai-capital-institute-portal" 
            element={
              <ProtectedRoute requiredRoles={['admin_todo']}>
                <AiCapitalInstitutePortal />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/person-search" 
            element={
              <ProtectedRoute requiredRoles={['admin']}>
                <PersonSearch />
              </ProtectedRoute>
            } 
          />
          
          {/* VALUATION section - Admin only */}
          <Route 
            path="/captables" 
            element={
              <ProtectedRoute requiredRoles={['admin']}>
                <CapTables />
              </ProtectedRoute>
            } 
          />
          
          {/* FINANCE section - Admin only */}
          <Route 
            path="/profitloss" 
            element={
              <ProtectedRoute requiredRoles={['admin']}>
                <ProfitLoss />
              </ProtectedRoute>
            } 
          />
          
          {/* PROJECTS section - Admin and User */}
          <Route 
            path="/projects" 
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']}>
                <Projects />
              </ProtectedRoute>
            } 
          />
          
          {/* CAMPAIGNS section - Admin only */}
          <Route 
            path="/campaign-dashboard" 
            element={
              <ProtectedRoute requiredRoles={['admin']}>
                <CampaignDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/campaigns" 
            element={
              <ProtectedRoute requiredRoles={['admin']}>
                <Campaigns />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/recipient-lists" 
            element={
              <ProtectedRoute requiredRoles={['admin']}>
                <RecipientLists />
              </ProtectedRoute>
            } 
          />
          
          {/* ADMINISTRATION section - Admin only */}
          <Route 
            path="/users-admin" 
            element={
              <ProtectedRoute requiredRoles={['admin']}>
                <UsersAdmin />
              </ProtectedRoute>
            } 
          />
          
          <Route path="*" element={<Navigate to="/projects" replace />} />
        </Routes>
      </Suspense>
    </CommsProvider>
  );
};

// Main App component
const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
        <div className="min-h-screen bg-gray-50">
          <AppContent />
        </div>
      </AuthProvider>
    </Router>
  );
};

export default App;
